import { useEffect, useState } from "react";
import { api, EvidenceBadge, PILLARS, FORMATS } from "@/lib/api";
import { toast } from "sonner";
import { Wand2, ThumbsUp, ThumbsDown, ArrowRight, RefreshCcw, Trash2 } from "lucide-react";

export default function Ideas() {
  const [ideas, setIdeas] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/ideas").then((r) => setIdeas(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/generate_ideas", { count: 6, focus: "" });
      toast.success(`Generated ${data.count} ideas`);
      load();
    } catch (e) {
      toast.error("Idea generation failed. Check LLM key balance.");
    } finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    await api.patch(`/ideas/${id}`, { status });
    load();
  };

  const removeIdea = async (id) => {
    if (!confirm("Delete this idea permanently?")) return;
    await api.delete(`/ideas/${id}`);
    toast.success("Deleted");
    load();
  };

  const convertToScript = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/generate_script", { idea_id: id, duration: 45 });
      toast.success("Script generated");
      window.location.href = `/scripts/${data.id}`;
    } catch (e) {
      toast.error("Script generation failed");
    } finally { setBusy(false); }
  };

  const filtered = ideas.filter((i) => filter === "all" || i.status === filter);

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Idea Intelligence</div>
          <h1 className="text-5xl font-black tracking-tight leading-none" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Ideas</h1>
        </div>
        <button data-testid="generate-ideas-btn" onClick={generate} disabled={busy}
          className="px-4 py-2.5 rounded-md font-semibold text-sm text-neutral-900 flex items-center gap-2 disabled:opacity-50" style={{ background: "#D4FF00" }}>
          {busy ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Generate 6 ideas
        </button>
      </div>

      <div className="flex gap-2 mb-6">
        {["all", "inbox", "shortlisted", "approved", "converted_to_script", "rejected"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider transition-colors ${filter === f ? "bg-neutral-900 text-white" : "bg-white border border-neutral-200 text-neutral-700"}`}>
            {f.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((i) => (
          <div key={i.id} data-testid={`idea-card-${i.id}`} className="bg-white border border-neutral-200 rounded-lg p-5 hover:-translate-y-[1px] transition-transform">
            <div className="flex items-start justify-between gap-2">
              <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{PILLARS[i.pillar] || i.pillar}</div>
              <div className="flex items-center gap-2">
                <EvidenceBadge confidence={i.confidence} />
                <button data-testid={`del-idea-${i.id}`} onClick={() => removeIdea(i.id)} className="text-neutral-400 hover:text-red-600 transition-colors" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div className="mt-2 font-bold text-lg leading-snug" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{i.title}</div>
            <div className="text-sm text-neutral-600 mt-1 line-clamp-3">{i.premise}</div>
            <div className="mt-3 text-xs text-neutral-500">
              <div>Hook: <span className="text-neutral-800">{i.hook}</span></div>
              <div className="mt-1">Format: {FORMATS[i.format] || i.format} · Auth {i.authenticity_score}/100</div>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <button onClick={() => setStatus(i.id, "shortlisted")} data-testid={`shortlist-${i.id}`} className="flex-1 text-xs font-semibold py-1.5 rounded border border-neutral-300 hover:bg-neutral-50 transition-colors flex items-center justify-center gap-1"><ThumbsUp className="w-3 h-3" />Strong</button>
              <button onClick={() => setStatus(i.id, "rejected")} data-testid={`reject-${i.id}`} className="flex-1 text-xs font-semibold py-1.5 rounded border border-neutral-300 hover:bg-neutral-50 transition-colors flex items-center justify-center gap-1"><ThumbsDown className="w-3 h-3" />Skip</button>
              <button onClick={() => convertToScript(i.id)} data-testid={`to-script-${i.id}`} disabled={busy}
                className="text-xs font-semibold py-1.5 px-3 rounded text-neutral-900 flex items-center gap-1 disabled:opacity-50" style={{ background: "#D4FF00" }}>
                Script <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-16 border border-dashed border-neutral-300 rounded-lg">
            <div className="text-neutral-500 text-sm">No ideas yet. Generate a batch with AI.</div>
          </div>
        )}
      </div>
    </div>
  );
}
